import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { matchPath, useLocation } from "react-router-dom";
import {
  DEFAULT_OG_IMAGE,
  getCanonicalUrl,
  SITE_NAME,
  SITE_URL,
} from "@/config/site";
import { gtagStub, sanitizeGtagParams } from "@/lib/ga4";
import { captureAppAttribution } from "@/lib/landingAttribution";

const DEFAULT_TITLE = `${SITE_NAME} - Now We're Talking | Voice AI Agents`;
const DEFAULT_DESCRIPTION =
  "Help Genie helps businesses deploy voice AI agents for websites, phones, lead capture, customer support and after-hours coverage.";

// GA4 property 529111462 — same property used by Help-Genie-Landing-Pages.
// Falls back to this when VITE_GA_MEASUREMENT_ID is not set in the build env so
// production tracking is never silently disabled by a missing Netlify variable.
const GA_MEASUREMENT_ID_FALLBACK = "G-30V2SYN5V7";

const SPA_ONLY_SOLUTION_ROUTES = [
  "/solutions/equipment",
  "/solutions/mining",
  "/solutions/clubs-associations",
] as const;

const INDEXABLE_ROUTES = [
  "/",
  "/consumer",
  "/how-it-works",
  "/explore-genies",
  "/pricing",
  "/contact",
  "/talk-to-genie",
  "/playbooks",
  "/insights",
  "/insights/:slug",
  "/privacy",
  "/terms",
  "/cookies",
  "/business",
  "/business/:slug",
  ...SPA_ONLY_SOLUTION_ROUTES,
  "/products",
  "/products/after-hours",
  "/help-genie-for-business-solutions",
  "/help-genie-for-developers",
  "/help-genie-for-partners",
  "/help-genie-for-consumers",
  "/discover",
  "/discover/:industrySlug",
  "/discover/:industrySlug/:subcategorySlug",
];

const NOINDEX_ROUTES = [
  "/discover/demo/:personaId",
  "/signin",
  "/signin/consumer",
  "/signup",
  "/creating-genie",
  "/forgot-password",
  "/auth/confirm",
  "/update-password",
  "/business-onboarding",
  "/consumer-onboarding",
  "/payment-success",
  "/agent/:url_name",
  "/live-agent/:url_name",
  "/genie/:url_name",
  "/portal/:slug",
  "/demo/:token",
];

const ROUTE_METADATA = [
  {
    path: "/",
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
  },
  {
    path: "/consumer",
    title: `${SITE_NAME} for Consumers | Talk to AI Genies`,
    description:
      "Talk to Help Genie voice assistants for instant answers, guided support and fast help across digital and phone experiences.",
  },
  {
    path: "/how-it-works",
    title: `How ${SITE_NAME} Works | Voice AI Setup, Knowledge and Deployment`,
    description:
      "See how Help Genie works from setup to deployment, including voice AI configuration, knowledge bases, embeds, phone lines and analytics.",
  },
  {
    path: "/explore-genies",
    title: `Explore AI Genie Examples | ${SITE_NAME}`,
    description:
      "Explore Help Genie voice assistant examples, use cases and deployment ideas across industries, support flows and customer journeys.",
  },
  {
    path: "/pricing",
    title: `${SITE_NAME} Pricing | Voice AI Plans for Growing Businesses`,
    description:
      "Review Help Genie pricing for voice AI agents, including plans, features, call allowances and tools for support, sales and lead capture.",
  },
  {
    path: "/contact",
    title: `Contact ${SITE_NAME}`,
    description:
      "Contact Help Genie to ask questions, request a walkthrough, discuss pricing or get help planning your voice AI deployment.",
  },
  {
    path: "/talk-to-genie",
    title: `Talk to a Genie | ${SITE_NAME}`,
    description:
      "Try Help Genie through a live link, phone call, QR code or web embed and experience the voice AI journey yourself.",
  },
  {
    path: "/playbooks",
    title: `${SITE_NAME} Playbooks | Voice AI Use Cases and Templates`,
    description:
      "Browse Help Genie playbooks and proven voice AI templates for customer support, lead capture, after-hours coverage and more.",
  },
  {
    path: "/insights",
    title: `${SITE_NAME} Insights | Voice AI Guides, Strategy and Updates`,
    description:
      "Read Help Genie insights, guides and best practices for voice AI, conversational design, deployment strategy and customer experience.",
  },
  {
    path: "/privacy",
    title: `Privacy Policy | ${SITE_NAME}`,
    description:
      "Read the Help Genie privacy policy, including how data is collected, used, protected and managed across the platform.",
  },
  {
    path: "/terms",
    title: `Terms of Service | ${SITE_NAME}`,
    description:
      "Review the Help Genie terms of service covering platform use, subscriptions, responsibilities and legal terms.",
  },
  {
    path: "/cookies",
    title: `Cookie Policy | ${SITE_NAME}`,
    description:
      "Understand how Help Genie uses cookies and related technologies for analytics, functionality and product performance.",
  },
  {
    path: "/products",
    title: `${SITE_NAME} Products | Voice AI Solutions for Business`,
    description:
      "Explore Help Genie products built for voice AI customer support, lead capture, after-hours coverage and scalable customer conversations.",
  },
  {
    path: "/help-genie-for-business-solutions",
    title: `${SITE_NAME} for Business`,
    description:
      "See how businesses use Help Genie to automate support, capture leads, answer calls and create better customer experiences.",
  },
  {
    path: "/help-genie-for-developers",
    title: `${SITE_NAME} for Developers`,
    description:
      "Learn how developers can integrate Help Genie into products, websites and workflows with flexible deployment options and APIs.",
  },
  {
    path: "/help-genie-for-partners",
    title: `${SITE_NAME} for Partners`,
    description:
      "Discover how agencies, consultants and partners can deploy Help Genie for clients and scale voice AI solutions.",
  },
  {
    path: "/help-genie-for-consumers",
    title: `${SITE_NAME} for Consumers`,
    description:
      "Explore how Help Genie helps consumers get fast answers, guided service and convenient voice-first interactions.",
  },
  {
    path: "/discover",
    title: `Discover Voice AI Solutions | ${SITE_NAME}`,
    description:
      "Discover voice AI use cases and tailored deployment ideas from Help Genie for your business, industry and customer journey.",
  },
];

const getRouteMetadata = (pathname: string) => {
  for (const route of ROUTE_METADATA) {
    if (matchPath({ path: route.path, end: true }, pathname)) {
      return route;
    }
  }

  return null;
};

const matchesRouteList = (pathname: string, routes: readonly string[]) =>
  routes.some((route) => matchPath({ path: route, end: true }, pathname));

const isNonCanonicalSolutionRoute = (pathname: string) =>
  Boolean(matchPath({ path: "/solutions", end: true }, pathname)) ||
  (Boolean(matchPath({ path: "/solutions/:slug", end: true }, pathname)) &&
    !matchesRouteList(pathname, SPA_ONLY_SOLUTION_ROUTES));

const isLocalhost = () =>
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1";

const SiteMetadataManager = () => {
  const location = useLocation();
  const gaMeasurementId =
    import.meta.env.VITE_GA_MEASUREMENT_ID ?? GA_MEASUREMENT_ID_FALLBACK;
  const pathname = location.pathname;
  const shouldEmitCanonical = !isNonCanonicalSolutionRoute(pathname);
  const canonicalUrl = shouldEmitCanonical ? getCanonicalUrl(pathname) : null;
  const routeMetadata = getRouteMetadata(pathname);
  const isNoindexRoute = matchesRouteList(pathname, NOINDEX_ROUTES);
  const isIndexableRoute =
    matchesRouteList(pathname, INDEXABLE_ROUTES) && !isNoindexRoute;
  const title = routeMetadata?.title || DEFAULT_TITLE;
  const description = routeMetadata?.description || DEFAULT_DESCRIPTION;
  const robots = isIndexableRoute ? "index,follow" : "noindex,nofollow";
  const websiteJsonLd =
    pathname === "/"
      ? {
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: SITE_NAME,
          url: SITE_URL,
          description: DEFAULT_DESCRIPTION,
          potentialAction: {
            "@type": "SearchAction",
            target: `${SITE_URL}/insights?query={search_term_string}`,
            "query-input": "required name=search_term_string",
          },
        }
      : null;
  const organizationJsonLd =
    pathname === "/"
      ? {
          "@context": "https://schema.org",
          "@type": "Organization",
          name: SITE_NAME,
          url: SITE_URL,
          logo: DEFAULT_OG_IMAGE,
          contactPoint: {
            "@type": "ContactPoint",
            contactType: "sales",
            email: "contact@helpgenie.ai",
          },
        }
      : null;

  useEffect(() => {
    if (!gaMeasurementId || isLocalhost()) {
      return;
    }

    if (!window.dataLayer) {
      window.dataLayer = [];
    }

    // Must push `arguments`, not an array: see gtagStub in @/lib/ga4.
    if (!window.gtag) {
      window.gtag = gtagStub;
    }

    if (!document.querySelector(`script[data-ga-id="${gaMeasurementId}"]`)) {
      const script = document.createElement("script");
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${gaMeasurementId}`;
      script.dataset.gaId = gaMeasurementId;
      document.head.appendChild(script);
    }

    window.gtag("js", new Date());
    window.gtag("config", gaMeasurementId, {
      send_page_view: false,
      anonymize_ip: true,
    });
  }, [gaMeasurementId]);

  // Ad, email or social links that land straight on an app route (not a
  // marketing page) still get their gclid / utm_* kept for sign-up.
  useEffect(() => {
    captureAppAttribution();
  }, [location.search]);

  useEffect(() => {
    if (!gaMeasurementId || isLocalhost()) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      const pageView = sanitizeGtagParams({
        page_title: document.title,
        page_location: window.location.href,
        page_path: `${location.pathname}${location.search}${location.hash}`,
        // Supplying this explicitly prevents GA from inferring and retaining a
        // same-origin referrer that contained a legacy bearer query string.
        page_referrer: document.referrer,
      });
      window.gtag?.("event", "page_view", pageView);
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [gaMeasurementId, location.hash, location.pathname, location.search]);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:image" content={DEFAULT_OG_IMAGE} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
      {websiteJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(websiteJsonLd)}
        </script>
      )}
      {organizationJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(organizationJsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default SiteMetadataManager;
